import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { Router, ActivatedRoute } from '@angular/router';
import { ApiService } from '../services/api.service';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

@Component({
  standalone: true,
  selector: 'app-product',
  templateUrl: './product.component.html',
  styleUrl: './product.component.css',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatAutocompleteModule,
    MatExpansionModule,
    MatSelectModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule
  ]
})
export class ProductComponent implements OnInit {
  productForm: FormGroup;
  productId: number | null = null;
  isEditMode: boolean = false;
  loading: boolean = false;
  error: string | null = null;
  vendors: any[] = [];
  productTypes: any[] = [];
  unitTypes: string[] = [];
  activityTypes: any[] = [];
  filteredVendors: Observable<any[]>[] = [];

  matchedPairSearch: string = '';

  get filteredMatchedPairIndices(): number[] {
    if (!this.matchedPairSearch.trim()) {
      return this.matchedPairsArray.controls.map((_, i) => i);
    }
    const q = this.matchedPairSearch.trim().toLowerCase();
    return this.matchedPairsArray.controls
      .map((ctrl, i) => ({ ctrl, i }))
      .filter(({ ctrl }) => {
        const solidPhaseProductId = (ctrl.get('solidPhaseProductId')?.value ?? '').toString();
        const detectionProductId = (ctrl.get('detectionProductId')?.value ?? '').toString();
        return solidPhaseProductId.includes(q) || detectionProductId.includes(q);
      })
      .map(({ i }) => i);
  }

  get totalQuantityOnHand(): number {
    return this.vendorProductsArray.controls.reduce((sum, vpCtrl) => {
      const lotNums = vpCtrl.get('lotNums') as FormArray;
      return sum + lotNums.controls.reduce((s, ctrl) => s + (ctrl.get('quantityOnHand')?.value ?? 0), 0);
    }, 0);
  }

  get totalQuantityReserved(): number {
    return this.vendorProductsArray.controls.reduce((sum, vpCtrl) => {
      const lotNums = vpCtrl.get('lotNums') as FormArray;
      return sum + lotNums.controls.reduce((s, ctrl) => s + (ctrl.get('quantityReserved')?.value ?? 0), 0);
    }, 0);
  }

  get totalQuantityAvailable(): number {
    return this.totalQuantityOnHand - this.totalQuantityReserved;
  }

  priceHistoryDataSource = new MatTableDataSource<any>([]);
  priceHistoryLoading: boolean = false;
  priceHistoryColumns: string[] = ['date', 'customer', 'unitPrice', 'costPerUnit', 'net', 'lotNumber', 'salesOrderNumber'];

  @ViewChild(MatSort) set priceHistorySort(s: MatSort) {
    if (s) { this.priceHistoryDataSource.sort = s; }
  }

  @ViewChild(MatPaginator) set priceHistoryPaginator(p: MatPaginator) {
    if (p) { this.priceHistoryDataSource.paginator = p; }
  }

  applyPriceHistoryFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.priceHistoryDataSource.filter = filterValue.trim().toLowerCase();

    if (this.priceHistoryDataSource.paginator) {
      this.priceHistoryDataSource.paginator.firstPage();
    }
  }

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private apiService: ApiService,
    private cdr: ChangeDetectorRef
  ) {
    this.productForm = this.fb.group({
      qboProductId: [null],
      name: ['', Validators.required],
      description: [''],
      catalogNum: [''],
      dataSheetDocId: [''],
      productType: [null],
      vendorProducts: this.fb.array([]),
      matchedPairs: this.fb.array([])
    });
  }

  ngOnInit(): void {
    this.loadVendors();
    this.loadProductTypes();
    this.loadUnitTypes();
    this.loadActivityTypes();

    // Check if there's an id parameter in the route
    this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      if (id && !isNaN(+id)) {
        this.productId = +id;
        this.isEditMode = false;
        this.loadProduct(this.productId);
        this.loadPriceHistory(this.productId);
      } else {
        this.isEditMode = true;
        this.addVendorProduct();
      }
    });
  }

  get vendorProductsArray(): FormArray {
    return this.productForm.get('vendorProducts') as FormArray;
  }

  private createVendorProductGroup(vp?: any): FormGroup {
    const group = this.fb.group({
      id: [vp?.id ?? null],
      vendorId: [vp?.vendorId ?? null],
      vendorSearch: [vp?.vendorSearch ?? ''],
      catalogNum: [vp?.catalogNum ?? ''],
      lotNum: [vp?.lotNum ?? ''],
      lotNums: this.fb.array([])
    });
    const filtered = group.get('vendorSearch')!.valueChanges.pipe(
      startWith(group.get('vendorSearch')!.value || ''),
      map(value => this._filterVendors(value || ''))
    );
    this.filteredVendors.push(filtered);
    return group;
  }

  addVendorProduct(): void {
    this.vendorProductsArray.push(this.createVendorProductGroup());
  }

  removeVendorProduct(index: number): void {
    this.vendorProductsArray.removeAt(index);
    this.filteredVendors.splice(index, 1);
  }

  getLotNumsArray(vpIndex: number): FormArray {
    return this.vendorProductsArray.at(vpIndex).get('lotNums') as FormArray;
  }

  getVpFormGroup(vpIndex: number): FormGroup {
    return this.vendorProductsArray.at(vpIndex) as FormGroup;
  }

  getLotNumFormGroup(vpIndex: number, lotIndex: number): FormGroup {
    return this.getLotNumsArray(vpIndex).at(lotIndex) as FormGroup;
  }

  get allLotNumsCount(): number {
    return this.vendorProductsArray.controls.reduce((sum, vpCtrl) => {
      const lotNums = vpCtrl.get('lotNums') as FormArray;
      return sum + lotNums.length;
    }, 0);
  }

  private createLotNumGroup(ln?: any): FormGroup {
    return this.fb.group({
      id: [ln?.id ?? null],
      number: [ln?.number ?? ''],
      vendorLotNum: [ln?.vendorLotNum ?? ln?.VendorLotNum ?? ''],
      unitType: [ln?.unitType ?? ''],
      concentration: [ln?.concentration ?? ln?.Concentration ?? null],
      ActivityType: [ln?.ActivityType ?? ln?.activityType ?? ''],
      expirationDate: [this.formatDateForInput(ln?.expirationDate)],
      manufactureDate: [this.formatDateForInput(ln?.manufactureDate)],
      quantityReceived: [ln?.quantityReceived ?? 0, Validators.min(0)],
      quantityOnHand: [ln?.quantityOnHand ?? 0, Validators.min(0)],
      quantityReserved: [ln?.quantityReserved ?? 0, Validators.min(0)],
      dataSheetDocId: [ln?.dataSheetDocId ?? ''],
      notes: [ln?.notes ?? '']
    });
  }

  private formatDateForInput(date: any): string {
    if (!date) return '';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    return d.toISOString().split('T')[0];
  }

  addLotNum(vpIndex: number): void {
    this.getLotNumsArray(vpIndex).insert(0, this.createLotNumGroup());
  }

  removeLotNum(vpIndex: number, lotIndex: number): void {
    this.getLotNumsArray(vpIndex).removeAt(lotIndex);
  }

  fetchLatestLotNumber(vpIndex: number, lotIndex: number): void {
    if (!this.productId) {
      this.error = 'Save the product first to generate a lot number.';
      return;
    }

    this.apiService.get<any>(`product/${this.productId}/latestlotnumber`).subscribe({
      next: (response) => {
        this.getLotNumFormGroup(vpIndex, lotIndex).patchValue({ number: response?.lotNumber ?? '' });
      },
      error: (err) => {
        this.error = err?.error?.error || 'Unable to determine next lot number. Please enter manually.';
        console.error('Error fetching latest lot number:', err);
      }
    });
  }

  get matchedPairsArray(): FormArray {
    return this.productForm.get('matchedPairs') as FormArray;
  }

  private createMatchedPairGroup(mp?: any): FormGroup {
    return this.fb.group({
      id: [mp?.id ?? null],
      solidPhaseProductId: [mp?.solidPhaseProductId ?? null],
      detectionProductId: [mp?.detectionProductId ?? null],
      comments: [mp?.comments ?? '']
    });
  }

  addMatchedPair(): void {
    this.matchedPairsArray.insert(0, this.createMatchedPairGroup());
  }

  removeMatchedPair(index: number): void {
    this.matchedPairsArray.removeAt(index);
  }

  loadProduct(id: number): void {
    this.loading = true;
    this.error = null;

    console.log(`Loading product with ID: ${id}`);

    this.apiService.get<any>(`product/${id}`).subscribe({
      next: (data) => {
        console.log('Product data received:', data);
        
        // Handle both camelCase and PascalCase from API
        const product = {
          qboProductId: data.qBO_Product_Id || data.QBO_Product_Id || data.qboProductId,
          name: data.name || data.Name,
          description: data.description || data.Description,
          catalogNum: data.catalogNum || data.CatalogNum,
          dataSheetDocId: (data.dataSheets?.[0]?.docId || data.DataSheets?.[0]?.DocId || '').toString(),
          productType: data.productType || data.ProductType || null
        };

        // Populate main form fields
        this.productForm.patchValue(product);

        // Clear and repopulate vendorProducts FormArray
        while (this.vendorProductsArray.length) {
          this.vendorProductsArray.removeAt(0);
        }
        this.filteredVendors = [];

        const rawVps: any[] = data.vendorProducts || data.VendorProducts ||
          (data.vendorProduct || data.VendorProduct ? [data.vendorProduct || data.VendorProduct] : []);

        if (rawVps.length === 0) {
          this.addVendorProduct();
        } else {
          rawVps.forEach((vp: any) => {
            const vendorObj = vp?.vendor || vp?.Vendor ||
              this.vendors.find(v => (v.id || v.Id) === (vp?.vendorId || vp?.VendorId)) || null;
            this.vendorProductsArray.push(this.createVendorProductGroup({
              id: vp?.id || vp?.Id || null,
              vendorId: vp?.vendorId || vp?.VendorId || null,
              vendorSearch: vendorObj,
              catalogNum: vp?.catalogNum || vp?.CatalogNum || '',
              lotNum: vp?.lotNum || vp?.LotNum || ''
            }));
            const vpIndex = this.vendorProductsArray.length - 1;
            const rawLotNums: any[] = vp?.lotNums || vp?.LotNums || [];
            rawLotNums.forEach((ln: any) => {
              this.getLotNumsArray(vpIndex).push(this.createLotNumGroup(ln));
            });
          });
        }

        // Clear and repopulate matchedPairs FormArray
        while (this.matchedPairsArray.length) {
          this.matchedPairsArray.removeAt(0);
        }
        const rawMatchedPairs: any[] = data.matchedPairs || data.MatchedPairs || [];
        rawMatchedPairs.forEach((mp: any) => {
          this.matchedPairsArray.push(this.createMatchedPairGroup({
            id: mp?.id || mp?.Id || null,
            solidPhaseProductId: mp?.solidPhaseProductId || mp?.SolidPhaseProductId || null,
            detectionProductId: mp?.detectionProductId || mp?.DetectionProductId || null,
            comments: mp?.comments || mp?.Comments || ''
          }));
        });

        this.loading = false;
        // Force change detection
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error loading product:', err);
        
        if (err.status === 404) {
          this.error = `Product with ID ${id} not found.`;
        } else if (err.status === 401) {
          this.error = 'Unauthorized. Please log in again.';
        } else {
          this.error = `Failed to load product: ${err.message || 'Unknown error'}`;
        }
        
        this.loading = false;
        // Force change detection
        this.cdr.detectChanges();
      }
    });
  }

  loadProductTypes(): void {
    this.apiService.get<any[]>('product/productTypes/').subscribe({
      next: (data) => {
        this.productTypes = data || [];
      },
      error: (err) => {
        console.error('Error loading product types:', err);
        this.productTypes = [];
      }
    });
  }

  loadUnitTypes(): void {
    this.apiService.get<string[]>('product/unitTypes').subscribe({
      next: (data) => { this.unitTypes = data || []; },
      error: (err) => { console.error('Error loading unit types:', err); this.unitTypes = []; }
    });
  }

  loadActivityTypes(): void {
    this.apiService.get<any[]>('product/activityTypes').subscribe({
      next: (data) => { this.activityTypes = data || []; },
      error: (err) => { console.error('Error loading activity types:', err); this.activityTypes = []; }
    });
  }

  loadVendors(): void {
    this.apiService.get<any[]>('vendor/all').subscribe({
      next: (data) => {
        this.vendors = data || [];
      },
      error: (err) => {
        console.error('Error loading vendors:', err);
        this.vendors = [];
      }
    });
  }

  loadPriceHistory(productId: number): void {
    this.priceHistoryLoading = true;
    this.apiService.get<any>(`product/${productId}/pricehistory`).subscribe({
      next: (data) => {
        const arr = Array.isArray(data) ? data : (data?.value || data?.data || []);
        this.priceHistoryDataSource.data = arr.map((item: any) => ({
          salesOrderId: item.salesOrderId ?? item.SalesOrderId,
          salesOrderNumber: item.salesOrderNumber ?? item.SalesOrderNumber,
          date: item.date ?? item.Date,
          customerId: item.customerId ?? item.CustomerId,
          customer: item.customerName ?? item.CustomerName,
          unitPrice: item.unitPrice ?? item.UnitPrice ?? 0,
          costPerUnit: item.costPerUnit ?? item.CostPerUnit ?? 0,
          netPercent: item.netPercent ?? item.NetPercent ?? 0,
          lotNumber: item.lotNumber ?? item.LotNumber
        }));
        this.priceHistoryLoading = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error loading price history:', err);
        this.priceHistoryLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  formatNet(value: number | null | undefined): string {
    const net = value ?? 0;
    return `${net.toFixed(2)}%`;
  }

  goToSalesOrder(salesOrderId: number | null | undefined): void {
    if (salesOrderId) {
      this.router.navigate(['/orders', salesOrderId]);
    }
  }

  goToCustomer(customerId: number | null | undefined): void {
    if (customerId) {
      this.router.navigate(['/customers', customerId]);
    }
  }

  private _filterVendors(value: string | any): any[] {
    if (typeof value !== 'string') {
      return this.vendors;
    }
    const filterValue = value.toLowerCase();
    return this.vendors.filter(vendor => {
      const name = vendor.name || vendor.Name || '';
      const id = (vendor.id || vendor.Id || '').toString();
      return name.toLowerCase().includes(filterValue) || id.includes(filterValue);
    });
  }

  displayVendor(vendor: any): string {
    return vendor ? `${vendor.name || vendor.Name || ''} (ID: ${vendor.id || vendor.Id || ''})` : '';
  }

  onVendorSelected(vendor: any, index: number): void {
    if (vendor) {
      const vendorId = vendor.id || vendor.Id;
      this.vendorProductsArray.at(index).get('vendorId')?.patchValue(vendorId);
    }
  }

  clearVendor(index: number): void {
    this.vendorProductsArray.at(index).patchValue({
      vendorSearch: '',
      vendorId: null,
      catalogNum: '',
      lotNum: ''
    });
  }

  enterEditMode(): void {
    this.isEditMode = true;
    this.vendorProductsArray.controls.forEach(vpCtrl => {
      const lotNums = vpCtrl.get('lotNums') as FormArray;
      lotNums.controls.forEach(lnCtrl => lnCtrl.get('ActivityType')?.enable({ emitEvent: false }));
    });
  }

  onSubmit() {
    if (this.productForm.valid) {
      const formValue = this.productForm.getRawValue();
      var payload: any = {
        qBO_Product_Id: formValue.qboProductId,
        name: formValue.name,
        description: formValue.description,
        catalogNum: formValue.catalogNum,
        productType: formValue.productType ?? null,
        vendorProducts: this.vendorProductsArray.controls
          .filter(ctrl => ctrl.get('vendorId')?.value)
          .map(ctrl => ({
            id: ctrl.get('id')?.value || 0,
            vendorId: ctrl.get('vendorId')?.value,
            catalogNum: ctrl.get('catalogNum')?.value,
            lotNum: ctrl.get('lotNum')?.value,
            lotNums: (ctrl.get('lotNums') as FormArray).controls.map(lnCtrl => ({
              id: lnCtrl.get('id')?.value || 0,
              number: lnCtrl.get('number')?.value,
              vendorLotNum: lnCtrl.get('vendorLotNum')?.value || null,
              unitType: lnCtrl.get('unitType')?.value || null,
              concentration: lnCtrl.get('concentration')?.value,
              ActivityType: lnCtrl.get('ActivityType')?.value || null,
              expirationDate: lnCtrl.get('expirationDate')?.value || null,
              manufactureDate: lnCtrl.get('manufactureDate')?.value || null,
              quantityReceived: lnCtrl.get('quantityReceived')?.value,
              quantityOnHand: lnCtrl.get('quantityOnHand')?.value,
              quantityReserved: lnCtrl.get('quantityReserved')?.value,
              notes: lnCtrl.get('notes')?.value
            }))
          })),
        matchedPairs: this.matchedPairsArray.controls.map(ctrl => ({
          id: ctrl.get('id')?.value || 0,
          solidPhaseProductId: ctrl.get('solidPhaseProductId')?.value || 0,
          detectionProductId: ctrl.get('detectionProductId')?.value || null,
          comments: ctrl.get('comments')?.value || null
        }))
      };

      this.loading = true;
      this.error = null;

      if (this.productId) {
        payload.id = this.productId;
        this.apiService.put<any>(`product/${this.productId}`, payload).subscribe({
          next: () => {
            this.loading = false;
            this.router.navigate(['/products']);
          },
          error: (err) => {
            console.error('Error updating product:', err);
            this.error = `Failed to update product: ${err.message || 'Unknown error'}`;
            this.loading = false;
            this.cdr.detectChanges();
          }
        });
      } else {
        this.apiService.post<any>('product/', payload).subscribe({
          next: () => {
            this.loading = false;
            this.router.navigate(['/products']);
          },
          error: (err) => {
            console.error('Error creating product:', err);
            this.error = `Failed to create product: ${err.message || 'Unknown error'}`;
            this.loading = false;
            this.cdr.detectChanges();
          }
        });
      }
    }
  }

  viewVendor(index: number): void {
    const vendorId = this.vendorProductsArray.at(index).get('vendorId')?.value;
    if (vendorId) {
      this.router.navigate(['/vendors', vendorId]);
    }
  }

  onCancel() {
    if (this.productId) {
      this.isEditMode = false;
      this.vendorProductsArray.controls.forEach(vpCtrl => {
        const lotNums = vpCtrl.get('lotNums') as FormArray;
        lotNums.controls.forEach(lnCtrl => lnCtrl.get('ActivityType')?.disable({ emitEvent: false }));
      });
    } else {
      this.router.navigate(['/products']);
    }
  }
}
